import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Check, ChevronRight, Leaf, Zap, Trash2, Utensils, Droplets, TreeDeciduous, Gem, Star, Gift, BarChart3 } from 'lucide-react';
interface OnboardingPageProps {
  onNavigate: (page: string) => void;
}
export function OnboardingPage({
  onNavigate
}: OnboardingPageProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [quizScore, setQuizScore] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<Record<number, number>>({});
  const [preferences, setPreferences] = useState({
    categories: [] as string[],
    frequency: '',
    difficulty: [] as string[]
  });
  const totalSteps = 12;
  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      onNavigate('home');
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const calculateScore = (answers: Record<number, number>) => {
    let score = 0;
    Object.values(answers).forEach(val => {
      score += val;
    });
    setQuizScore(score);
  };
  const handleQuizAnswer = (questionId: number, value: number) => {
    const newAnswers = {
      ...quizAnswers,
      [questionId]: value
    };
    setQuizAnswers(newAnswers);
    calculateScore(newAnswers);
    // Removed auto-advance - user must click Continue button
  };
  const togglePreference = (type: 'categories' | 'difficulty', value: string) => {
    setPreferences(prev => {
      const current = prev[type];
      const updated = current.includes(value) ? current.filter(item => item !== value) : [...current, value];
      return {
        ...prev,
        [type]: updated
      };
    });
  };
  const quizQuestions = [{
    id: 1,
    q: 'Come ti sposti di solito?',
    options: [{
      label: 'Auto privata',
      score: 5
    }, {
      label: 'Mezzi pubblici',
      score: 15
    }, {
      label: 'Bici o a piedi',
      score: 20
    }]
  }, {
    id: 2,
    q: 'Quanto spesso ricicli?',
    options: [{
      label: 'Raramente',
      score: 5
    }, {
      label: 'A volte',
      score: 10
    }, {
      label: 'Sempre',
      score: 20
    }]
  }, {
    id: 3,
    q: 'Quanti pasti vegetariani mangi a settimana?',
    options: [{
      label: '0-2',
      score: 5
    }, {
      label: '3-5',
      score: 15
    }, {
      label: 'Quasi tutti',
      score: 20
    }]
  }, {
    id: 4,
    q: 'Usi prodotti riutilizzabili (borraccia, borse)?',
    options: [{
      label: 'Mai',
      score: 0
    }, {
      label: 'A volte',
      score: 10
    }, {
      label: 'Sempre',
      score: 20
    }]
  }, {
    id: 5,
    q: 'Quanto sei attento al consumo energetico?',
    options: [{
      label: 'Poco',
      score: 5
    }, {
      label: 'Abbastanza',
      score: 15
    }, {
      label: 'Molto',
      score: 20
    }]
  }];
  // Helper function to get profile type based on quiz score
  const getProfileType = () => {
    if (quizScore >= 80) return {
      type: 'Eco-Champion',
      emoji: '🌟',
      desc: 'Sei già molto attento alla sostenibilità'
    };
    if (quizScore >= 50) return {
      type: 'Eco-Consapevole',
      emoji: '🌿',
      desc: 'Hai buone abitudini sostenibili'
    };
    return {
      type: 'Eco-Principiante',
      emoji: '🌱',
      desc: 'Stai iniziando il tuo percorso green'
    };
  };
  // Helper function to get personalized benefits based on profile
  const getPersonalizedBenefits = () => {
    const profile = getProfileType();
    if (profile.type === 'Eco-Champion') {
      return ['Scoprire azioni avanzate per massimizzare il tuo impatto', 'Ispirare altri utenti con il tuo esempio', 'Ottenere premi esclusivi per il tuo impegno'];
    }
    if (profile.type === 'Eco-Consapevole') {
      return ['Consolidare le tue buone abitudini con sfide quotidiane', 'Esplorare nuove aree di sostenibilità', 'Guadagnare crediti per le tue azioni green'];
    }
    return ['Iniziare con azioni semplici e immediate', 'Imparare passo dopo passo nuove abitudini sostenibili', 'Vedere i tuoi progressi crescere giorno dopo giorno'];
  };
  return <div className="min-h-screen bg-green-600 flex flex-col font-sans text-gray-900 relative overflow-hidden" data-id="element-1031">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-cover bg-center opacity-10 pointer-events-none" style={{
      backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/wsCbUWp4TF3ni7DwEfb9qP/hand-drawn-turtle-shell-pattern_23-2150598661.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center'
    }} data-id="element-1032" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 px-6 py-4 z-30 bg-green-600" data-id="element-1033">
        <div className="flex items-center gap-4" data-id="element-1034">
          {/* Logo */}
          <div className="flex-shrink-0" data-id="element-1035">
            <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className="h-8 w-auto brightness-0 invert" data-id="element-1036" />
          </div>

          {/* Progress Section */}
          <div className="flex-1" data-id="element-1037">
            <div className="flex items-center justify-between mb-2" data-id="element-1038">
              <span className="text-xs text-green-100 font-normal" data-id="element-1039">
                Passo {currentStep} di {totalSteps}
              </span>
              <span className="text-xs text-green-100 font-normal" data-id="element-1040">
                {Math.round(currentStep / totalSteps * 100)}%
              </span>
            </div>
            <div className="w-full bg-green-800/30 rounded-full h-1.5" data-id="element-1041">
              <div className="bg-white h-full rounded-full transition-all duration-500 ease-out" style={{
              width: `${currentStep / totalSteps * 100}%`
            }} data-id="element-1042" />
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-col items-center p-6 relative z-10 pt-28" data-id="element-1043">
        <div className="w-full max-w-md" data-id="element-1044">
          {/* Section Title - Outside white box */}
          {currentStep >= 2 && currentStep <= 6 && <div className="mb-4" data-id="element-1045">
              <h2 className="text-2xl font-normal text-white mb-1 flex items-center gap-2" data-id="element-1046">
                <span data-id="element-1047">🧭</span>
                Scopri il tuo profilo
              </h2>
              <p className="text-sm text-green-100 font-normal" data-id="element-1048">
                Domanda {currentStep - 1} di 5
              </p>
            </div>}

          {/* Section Title for Goals - Outside white box */}
          {currentStep >= 9 && currentStep <= 11 && <div className="mb-4" data-id="element-1049">
              <h2 className="text-2xl font-normal text-white mb-1 flex items-center gap-2" data-id="element-1050">
                <span data-id="element-1051">🎯</span>I tuoi obiettivi
              </h2>
              <p className="text-sm text-green-100 font-normal" data-id="element-1052">
                Domanda {currentStep - 8} di 3
              </p>
            </div>}

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl p-6 min-h-[400px] flex flex-col relative overflow-hidden" data-id="element-1053">
            {/* Step 1: Welcome */}
            {currentStep === 1 && <div className="flex-1 flex flex-col items-center justify-center text-center animate-fade-in" data-id="element-1054">
                <div className="text-6xl mb-6 animate-bounce-slow" data-id="element-1055">👋</div>
                <h1 className="text-xl font-normal text-gray-900 mb-3" data-id="element-1056">
                  Benvenuto in Greenback
                </h1>
                <p className="text-sm text-gray-600 max-w-xs mx-auto font-normal" data-id="element-1057">
                  Il tuo percorso verso la sostenibilità inizia qui. Scopri come
                  le tue azioni quotidiane possono fare la differenza.
                </p>
              </div>}

            {/* Steps 2-6: Quiz Questions */}
            {currentStep >= 2 && currentStep <= 6 && <div className="flex-1 flex flex-col animate-fade-in" data-id="element-1058">
                <div className="flex-1 flex flex-col justify-center" data-id="element-1059">
                  {(() => {
                const question = quizQuestions[currentStep - 2];
                return <div data-id="element-1060">
                        <p className="text-sm font-medium text-gray-900 mb-4" data-id="element-1061">
                          {question.q}
                        </p>

                        <div className="space-y-3" data-id="element-1062">
                          {question.options.map((opt, idx) => <button key={idx} onClick={() => handleQuizAnswer(question.id, opt.score)} className={`w-full text-left text-sm font-normal py-3 px-4 rounded-xl border transition-all ${quizAnswers[question.id] === opt.score ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:border-green-300 hover:bg-green-50'}`} data-id="element-1063">
                              {opt.label}
                            </button>)}
                        </div>
                      </div>;
              })()}
                </div>
              </div>}

            {/* Step 7: Profile Summary */}
            {currentStep === 7 && <div className="flex-1 flex flex-col items-center justify-center animate-fade-in px-2" data-id="element-1064">
                <div className="text-5xl mb-4" data-id="element-1065">{getProfileType().emoji}</div>
                <h2 className="text-xl font-normal text-gray-900 mb-2" data-id="element-1066">
                  Il tuo profilo: {getProfileType().type}
                </h2>
                <p className="text-sm text-gray-600 mb-6 font-normal" data-id="element-1067">
                  {getProfileType().desc}
                </p>

                <div className="w-full bg-green-50 rounded-xl p-4 mb-6 border border-green-100" data-id="element-1068">
                  <div className="flex items-center justify-center mb-3" data-id="element-1069">
                    <span className="text-xs text-gray-600 font-normal mr-2" data-id="element-1070">
                      Punteggio Sostenibilità
                    </span>
                    <span className="text-2xl font-normal text-green-600" data-id="element-1071">
                      {quizScore}/100
                    </span>
                  </div>
                </div>

                <div className="w-full mb-6" data-id="element-1072">
                  <h3 className="text-sm font-medium text-gray-900 mb-3 text-center" data-id="element-1073">
                    Greenback ti aiuterà a:
                  </h3>
                  <div className="space-y-2" data-id="element-1074">
                    {getPersonalizedBenefits().map((benefit, idx) => <div key={idx} className="flex items-start gap-2 text-left" data-id="element-1075">
                        <span className="text-green-600 flex-shrink-0 mt-0.5" data-id="element-1076">
                          ✓
                        </span>
                        <p className="text-sm text-gray-700 font-normal" data-id="element-1077">
                          {benefit}
                        </p>
                      </div>)}
                  </div>
                </div>
              </div>}

            {/* Step 8: Intro - Obiettivi */}
            {currentStep === 8 && <div className="flex-1 flex flex-col items-center justify-center text-center animate-fade-in" data-id="element-1078">
                <div className="text-5xl mb-6" data-id="element-1079">🎯</div>
                <h2 className="text-xl font-normal text-gray-900 mb-3" data-id="element-1080">
                  I tuoi obiettivi
                </h2>
                <p className="text-sm text-gray-600 max-w-sm mx-auto font-normal leading-relaxed" data-id="element-1081">
                  Ora definiamo insieme i tuoi obiettivi di sostenibilità e le
                  aree su cui vuoi concentrarti.
                </p>
              </div>}

            {/* Step 9: Preferences - Categories */}
            {currentStep === 9 && <div className="flex-1 flex flex-col animate-fade-in" data-id="element-1082">
                <h2 className="text-lg font-normal text-gray-900 mb-2" data-id="element-1083">
                  Cosa ti interessa?
                </h2>
                <p className="text-xs text-gray-500 mb-6 font-normal" data-id="element-1084">
                  Seleziona le categorie preferite
                </p>

                <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar" data-id="element-1085">
                  <div className="grid grid-cols-2 gap-3" data-id="element-1086">
                    {[{
                  id: 'transport',
                  label: 'Trasporti',
                  icon: '🚗'
                }, {
                  id: 'energy',
                  label: 'Energia',
                  icon: '⚡'
                }, {
                  id: 'waste',
                  label: 'Rifiuti',
                  icon: '♻️'
                }, {
                  id: 'food',
                  label: 'Cibo',
                  icon: '🍽️'
                }, {
                  id: 'water',
                  label: 'Acqua',
                  icon: '💧'
                }, {
                  id: 'nature',
                  label: 'Natura',
                  icon: '🌳'
                }].map(cat => <button key={cat.id} onClick={() => togglePreference('categories', cat.id)} className={`flex flex-col items-center justify-center p-3 rounded-xl border transition-all text-center h-24 ${preferences.categories.includes(cat.id) ? 'bg-green-50 border-green-500 ring-1 ring-green-500' : 'bg-white border-gray-200 hover:border-green-300'}`} data-id="element-1087">
                        <span className="text-2xl mb-1" data-id="element-1088">{cat.icon}</span>
                        <span className={`text-xs font-normal ${preferences.categories.includes(cat.id) ? 'text-green-700' : 'text-gray-700'}`} data-id="element-1089">
                          {cat.label}
                        </span>
                      </button>)}
                  </div>
                </div>
              </div>}

            {/* Step 10: Preferences - Frequency */}
            {currentStep === 10 && <div className="flex-1 flex flex-col animate-fade-in" data-id="element-1090">
                <h2 className="text-lg font-normal text-gray-900 mb-2" data-id="element-1091">
                  Il tuo obiettivo
                </h2>
                <p className="text-xs text-gray-500 mb-6 font-normal" data-id="element-1092">
                  Quante azioni vuoi completare a settimana?
                </p>

                <div className="flex-1 flex flex-col justify-center space-y-3" data-id="element-1093">
                  {['1-2 azioni (Inizio soft)', '3-5 azioni (Costante)', '6-10 azioni (Impegnato)', '10+ azioni (Eco-Warrior)'].map(opt => <button key={opt} onClick={() => {
                setPreferences({
                  ...preferences,
                  frequency: opt
                });
                setTimeout(handleNext, 300);
              }} className={`w-full text-left text-sm font-normal py-3 px-4 rounded-xl border transition-all ${preferences.frequency === opt ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:border-green-300 hover:bg-green-50'}`} data-id="element-1094">
                      {opt}
                    </button>)}
                </div>
              </div>}

            {/* Step 11: Preferences - Difficulty */}
            {currentStep === 11 && <div className="flex-1 flex flex-col animate-fade-in" data-id="element-1095">
                <h2 className="text-lg font-normal text-gray-900 mb-2" data-id="element-1096">
                  Livello di sfida
                </h2>
                <p className="text-xs text-gray-500 mb-6 font-normal" data-id="element-1097">
                  Che tipo di azioni preferisci?
                </p>

                <div className="flex-1 flex flex-col justify-center space-y-3" data-id="element-1098">
                  {['Facile', 'Medio', 'Difficile'].map(diff => <button key={diff} onClick={() => togglePreference('difficulty', diff)} className={`w-full flex items-center justify-between text-sm font-normal py-3 px-4 rounded-xl border transition-all ${preferences.difficulty.includes(diff) ? 'bg-green-100 text-green-800 border-green-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`} data-id="element-1099">
                      {diff}
                      {preferences.difficulty.includes(diff) && <Check className="h-4 w-4 text-green-600" data-id="element-1100" />}
                    </button>)}
                </div>
              </div>}

            {/* Step 12: Final Recap */}
            {currentStep === 12 && <div className="flex-1 flex flex-col items-center justify-center animate-fade-in text-center" data-id="element-1101">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-1102">
                  <Check className="h-8 w-8 text-green-600" strokeWidth={3} data-id="element-1103" />
                </div>
                <h2 className="text-xl font-normal text-gray-900 mb-2" data-id="element-1104">
                  Profilo completato!
                </h2>
                <p className="text-sm text-gray-600 mb-6 font-normal" data-id="element-1105">
                  Il tuo profilo sostenibile è pronto. Inizia subito a
                  completare azioni e guadagnare crediti!
                </p>

                <div className="w-full bg-gray-50 rounded-xl p-4 mb-6 text-left border border-gray-100" data-id="element-1106">
                  <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-200" data-id="element-1107">
                    <span className="text-xs text-gray-600 font-normal" data-id="element-1108">
                      Punteggio Sostenibilità
                    </span>
                    <span className="text-lg font-normal text-green-600" data-id="element-1109">
                      {quizScore}/100
                    </span>
                  </div>

                  <div className="space-y-2" data-id="element-1110">
                    <div className="flex justify-between" data-id="element-1111">
                      <span className="text-xs text-gray-500 font-normal" data-id="element-1112">
                        Obiettivo
                      </span>
                      <span className="text-xs font-normal text-gray-900" data-id="element-1113">
                        {preferences.frequency || '3-5 azioni'}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-1114">
                      <span className="text-xs text-gray-500 font-normal" data-id="element-1115">
                        Categorie
                      </span>
                      <span className="text-xs font-normal text-gray-900" data-id="element-1116">
                        {preferences.categories.length} selezionate
                      </span>
                    </div>
                  </div>
                </div>
              </div>}

            {/* Step 13: Tutorial */}
            {currentStep === 13 && <div className="flex-1 flex flex-col animate-fade-in" data-id="element-1117">
                <h2 className="text-lg font-normal text-center text-gray-900 mb-6" data-id="element-1118">
                  Come funziona Greenback
                </h2>

                <div className="flex-1 relative overflow-hidden" data-id="element-1119">
                  <div className="flex overflow-x-auto snap-x snap-mandatory h-full pb-4 gap-4 no-scrollbar" data-id="element-1120">
                    {[{
                  title: 'Completa azioni',
                  desc: 'Scegli tra centinaia di azioni sostenibili.',
                  icon: <Leaf className="h-10 w-10 text-green-500" data-id="element-1121" />,
                  color: 'bg-green-50'
                }, {
                  title: 'Guadagna crediti',
                  desc: 'Ottieni Punti Diamond 💎 e Stelle ⭐.',
                  icon: <Gem className="h-10 w-10 text-blue-500" data-id="element-1122" />,
                  color: 'bg-blue-50'
                }, {
                  title: 'Ottieni premi',
                  desc: 'Converti i tuoi crediti in buoni acquisto.',
                  icon: <Gift className="h-10 w-10 text-purple-500" data-id="element-1123" />,
                  color: 'bg-purple-50'
                }].map((slide, idx) => <div key={idx} className="flex-shrink-0 w-full flex flex-col items-center justify-center p-4 rounded-2xl border border-gray-100 snap-center text-center bg-white" data-id="element-1124">
                        <div className={`w-20 h-20 ${slide.color} rounded-full flex items-center justify-center mb-4`} data-id="element-1125">
                          {slide.icon}
                        </div>
                        <h3 className="text-base font-normal text-gray-900 mb-2" data-id="element-1126">
                          {slide.title}
                        </h3>
                        <p className="text-xs text-gray-500 max-w-xs font-normal" data-id="element-1127">
                          {slide.desc}
                        </p>
                      </div>)}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-2 pb-1" data-id="element-1128">
                    {[0, 1, 2].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-gray-300" data-id="element-1129" />)}
                  </div>
                </div>
              </div>}

            {/* Step 14: Summary */}
            {currentStep === 14 && <div className="flex-1 flex flex-col items-center justify-center animate-fade-in text-center" data-id="element-1130">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-1131">
                  <Check className="h-8 w-8 text-green-600" strokeWidth={3} data-id="element-1132" />
                </div>
                <h2 className="text-xl font-normal text-gray-900 mb-2" data-id="element-1133">
                  Tutto pronto!
                </h2>
                <p className="text-sm text-gray-600 mb-6 font-normal" data-id="element-1134">
                  Ecco il tuo profilo sostenibile iniziale.
                </p>

                <div className="w-full bg-gray-50 rounded-xl p-4 mb-6 text-left border border-gray-100" data-id="element-1135">
                  <div className="flex justify-between items-center mb-3 pb-3 border-b border-gray-200" data-id="element-1136">
                    <span className="text-xs text-gray-600 font-normal" data-id="element-1137">
                      Punteggio Sostenibilità
                    </span>
                    <span className="text-lg font-normal text-green-600" data-id="element-1138">
                      {quizScore}/100
                    </span>
                  </div>

                  <div className="space-y-2" data-id="element-1139">
                    <div className="flex justify-between" data-id="element-1140">
                      <span className="text-xs text-gray-500 font-normal" data-id="element-1141">
                        Obiettivo
                      </span>
                      <span className="text-xs font-normal text-gray-900" data-id="element-1142">
                        {preferences.frequency || '3-5 azioni'}
                      </span>
                    </div>
                    <div className="flex justify-between" data-id="element-1143">
                      <span className="text-xs text-gray-500 font-normal" data-id="element-1144">
                        Categorie
                      </span>
                      <span className="text-xs font-normal text-gray-900" data-id="element-1145">
                        {preferences.categories.length} selezionate
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg mb-4 font-normal italic" data-id="element-1146">
                  "Ogni piccolo gesto conta. Inizia oggi il tuo cambiamento!"
                </p>
              </div>}

            {/* Navigation Buttons */}
            <div className="mt-6 flex justify-between items-center pt-4 border-t border-gray-100" data-id="element-1147">
              {currentStep > 1 ? <button onClick={handleBack} className="text-gray-500 hover:text-gray-900 text-sm font-normal px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1148">
                  Indietro
                </button> : <div data-id="element-1149" />}

              <button onClick={handleNext} className="bg-green-600 text-white px-6 py-2.5 rounded-full text-sm font-normal hover:bg-green-700 transition-colors flex items-center gap-2" data-id="element-1150">
                {currentStep === totalSteps ? "Entra nell'app" : 'Continua'}
                {currentStep !== totalSteps && <ChevronRight className="h-4 w-4" data-id="element-1151" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}